import './privacy-preview.css';
import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import type { StorePrivacySettings } from './storePrivacy';

export function KreatorPrivacySection({ value, onChange, templateSettings }: {
  templateSettings?: Record<string, unknown>;
  value: StorePrivacySettings; onChange: (value: StorePrivacySettings) => void;
}) {
  const [preferences, setPreferences] = useState(false);
  const [compact, setCompact] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const previewStyle = { position: 'relative', left: 'auto', bottom: 'auto', zIndex: 'auto', maxHeight: 'none',
    width: compact ? 'min(100%, 374px)' : value.layout === 'card' ? 'min(100%, 440px)' : '100%',
    '--ef-button-bg': templateSettings?.buttonBackgroundColor ?? '#3b82f6',
    '--ef-button-text': templateSettings?.buttonTextColor ?? '#ffffff',
    '--ef-button-bg-hover': templateSettings?.buttonBackgroundHoverColor ?? '#3b82f6',
    '--ef-button-text-hover': templateSettings?.buttonTextHoverColor ?? '#ffffff',
  } as CSSProperties;
  const widePreview = value.layout === 'bar' && !compact;
  const fields = [
    ['title', 'Tytuł bannera', 100], ['message', 'Treść bannera', 1200],
    ['analyticsDescription', 'Opis cookies analitycznych', 500],
    ['marketingDescription', 'Opis cookies marketingowych', 500],
  ] as const;
  return <section className="card space-y-4" aria-label="Prywatność i cookies">
    <SectionHeader icon={ShieldCheck} title="Prywatność i cookies"
      description="Banner i wybór zgód dla tego sklepu. Podgląd nie publikuje zmian." />
    <Toggle label="Wyświetlaj banner cookies" checked={value.enabled}
      description="Domyślnie wyłączony. Włączenie zacznie obowiązywać po zapisaniu ustawień sklepu."
      onChange={e => onChange({ ...value, enabled: e.target.checked })} />
    <div><label className="block text-sm font-medium mb-2" htmlFor="privacy-layout">Układ bannera</label>
      <Select id="privacy-layout" value={value.layout} onChange={e => onChange({ ...value, layout: e.target.value === 'bar' ? 'bar' : 'card' })}>
        <option value="card">Kompaktowa karta</option><option value="bar">Pasek na całą szerokość</option>
      </Select></div>
    {fields.map(([key, label, maxLength]) => <div key={key}>
      <label className="block text-sm font-medium mb-2" htmlFor={`privacy-${key}`}>{label}</label>
      <textarea id={`privacy-${key}`} className="input w-full" rows={key === 'message' ? 3 : 2}
        maxLength={maxLength} value={value[key]} onChange={e => onChange({ ...value, [key]: e.target.value })} />
    </div>)}
    <div><label className="block text-sm font-medium mb-2" htmlFor="privacy-days">Zapamiętuj wybór przez (dni)</label>
      <input id="privacy-days" type="number" min={1} max={180} className="input w-32" value={value.retentionDays}
        onChange={e => onChange({ ...value, retentionDays: Number(e.target.value) })} /></div>
    <p className="text-sm text-text-secondary">Linki do polityki prywatności i cookies ustawisz w sekcji „Globalne linki”. Przed uruchomieniem trzeba również podłączyć skrypty innych wtyczek do zgód.</p>
    <p className="text-sm text-text-secondary">
      Ten banner jest też bramką zgód dla pomiaru wejść w sklepie (kanały sprzedaży w Cashflow).
      Sam pomiar włączasz raz na sklep w <Link to="/finance/settings" className="text-accent-primary underline">Finanse → Ustawienia</Link>.
    </p>
    <div className="flex flex-wrap items-center gap-3"><h3 className="font-medium">Podgląd treści</h3>
      <button type="button" className="btn-secondary" onClick={() => setCompact(!compact)}>{compact ? 'Widok szeroki' : 'Widok telefonu'}</button></div>
    {dismissed && <button type="button" className="btn-secondary" onClick={() => setDismissed(false)}>Pokaż podgląd bannera</button>}
    <div hidden={dismissed} style={previewStyle} className={`ef-privacy-panel ${widePreview ? 'ef-privacy-panel--bar' : ''}`}>
      <button type="button" aria-label="Zamknij podgląd bannera" className="ef-privacy-close" onClick={() => setDismissed(true)}><X size={16} /></button>
      <div className="ef-privacy-copy"><h2>{value.title}</h2><p>{value.message}</p></div>
      {preferences && <div className="ef-privacy-preferences">
        <p>Niezbędne: zawsze aktywne. Obsługują koszyk, bezpieczeństwo i zapis Twojego wyboru.</p>
        <label><input type="checkbox" defaultChecked={false} /><strong>Analityczne</strong><span>{value.analyticsDescription}</span></label>
        <label><input type="checkbox" defaultChecked={false} /><strong>Marketingowe</strong><span>{value.marketingDescription}</span></label>
        <button type="button" className="ef-privacy-primary" onClick={() => setPreferences(false)}>Zapisz wybrane</button>
      </div>}
      <p className="ef-privacy-links"><span className="underline">Polityka prywatności</span><span className="underline">Polityka cookies</span></p>
      <div className="ef-privacy-actions">
        <button type="button" className="ef-privacy-primary" onClick={() => setPreferences(false)}>Akceptuję wszystkie</button>
        <button type="button" className="ef-privacy-primary" onClick={() => setPreferences(false)}>Odrzucam opcjonalne</button>
        <button type="button" onClick={() => setPreferences(!preferences)}>Ustawienia</button>
      </div>
    </div>
    <p className="text-xs text-text-muted">Podgląd. Przyciski nie zapisują zgody w przeglądarce.</p>
  </section>;
}
